// 1. Framework / library imports
import { memo, useState } from "react";
import { ReactSVG } from "react-svg";

// 2. Helpers & Hooks
import { classNames, formatCurrency, useTranslate } from "helpers";
import { Errors } from "services/forms/validation/types";

// 4. UI Components
import { NumberField } from "ui/components/TextField/NumberField";
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Loader } from "ui/components/Loader";

// 5. Local types
import { DiscountFormProps } from "./types";


const DISCOUNT_TYPE_OPTIONS = [
    {
        value: 'percentage' as const,
        icon: "/media/icons/diagram.svg",
        name: "Postotak",
        description: "Popust u postotku od ukupne cijene",
    },
    {
        value: 'amount' as const,
        icon: "/media/icons/price.svg",
        name: "Iznos",
        description: "Popust kao fiksni iznos u eurima",
    },
];


const DiscountForm: React.FC<DiscountFormProps> = (props) => {
    const { show, formValues, setFormValues, onClose } = props;

    const { t } = useTranslate();
    const [errors, setErrors] = useState<Errors>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleDiscountValueChange = (value: number | null, name?: string) => {
        const discountValue = value ? value : null;
        setFormValues((prev) => {
            let netDiscount: number | null = null;
            if (prev.sellingPrice && discountValue !== null) {
                if (prev.discountType === 'percentage') {
                    netDiscount = (prev.sellingPrice * discountValue) / 100;
                } else {
                    netDiscount = discountValue;
                }
            }
            return { ...prev, discountValue, netDiscount };
        });
    };

    const handleDiscountTypeSelect = (value: 'percentage' | 'amount') => {
        setFormValues((prev) => {
            let netDiscount = prev.netDiscount;
            if (prev.sellingPrice && prev.discountValue) {
                if (value === 'percentage') {
                    netDiscount = (prev.sellingPrice * prev.discountValue) / 100;
                } else {
                    netDiscount = prev.discountValue;
                }
            }
            return { ...prev, discountType: value, netDiscount };
        });
    };

    /** Briše popust — resetira sve discount polja */
    const handleRemoveDiscount = () => {
        setFormValues((prev) => ({
            ...prev,
            discountType: null,
            discountValue: null,
            netDiscount: null,
        }));
        onClose();
    };

    const hasDiscount = formValues.netDiscount != null && formValues.netDiscount > 0;

    return (
        <Modal show={show} onHide={onClose} size="small" position="centered">
            <ModalHeader
                title={hasDiscount ? "Uredi popust" : t("admin.fleet.vehicle.add.discount.title")}
                icon="/media/images/add-icon.svg"
                onHide={onClose}
            />
            <ModalBody>
                {show && (
                    <>
                        <div className="rounded-sm p-3 mb-2">
                            <p className="text-sm leading-relaxed text-slate-500 mb-3">Odaberite način primjene popusta na ukupnu cijenu vozila.</p>

                            <div className="mb-2">
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Tip popusta
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                    {DISCOUNT_TYPE_OPTIONS.map((option) => {
                                        const isSelected = formValues.discountType === option.value;

                                        return (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => handleDiscountTypeSelect(option.value)}
                                                className={classNames(
                                                    "relative flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer",
                                                    isSelected
                                                        ? "border-blue-500 bg-blue-50"
                                                        : "border-slate-200 bg-white hover:border-slate-300"
                                                )}
                                            >
                                                <div
                                                    className={classNames(
                                                        "w-10 h-10 rounded-lg flex items-center justify-center",
                                                        isSelected ? "bg-blue-100" : "bg-slate-100"
                                                    )}
                                                >
                                                    <ReactSVG
                                                        src={option.icon}
                                                        className={classNames(
                                                            "w-5 h-5",
                                                            isSelected ? "stroke-blue-600" : "stroke-slate-600"
                                                        )}
                                                    />
                                                </div>
                                                <div className="text-left">
                                                    <h4
                                                        className={classNames(
                                                            "text-sm font-semibold",
                                                            isSelected ? "text-blue-900" : "text-slate-900"
                                                        )}
                                                    >
                                                        {option.name}
                                                    </h4>
                                                    <p
                                                        className={classNames(
                                                            "text-xs",
                                                            isSelected ? "text-blue-600" : "text-slate-500"
                                                        )}
                                                    >
                                                        {option.description}
                                                    </p>
                                                </div>
                                                {isSelected && (
                                                    <div className="absolute top-2 right-2">
                                                        <ReactSVG
                                                            src="/media/icons/check.svg"
                                                            className="w-4 h-4 fill-blue-500"
                                                        />
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {formValues.discountType && (
                                <div className="flex gap-3 mb-4">
                                    <div className="flex-1">
                                        <NumberField
                                            name="discountValue"
                                            label={formValues.discountType === 'percentage' ? "Popust (%)" : "Popust (iznos)"}
                                            placeholder={formValues.discountType === 'percentage' ? "Unesite postotak" : "Unesite iznos"}
                                            onChange={handleDiscountValueChange}
                                            error={errors.discountValue}
                                            value={formValues.discountValue}
                                            cssClassName="bg-white"
                                            groupAppend={formValues.discountType === 'percentage' ? "%" : "€"}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        {formValues.netDiscount != null && formValues.netDiscount > 0 && (
                                            <div className="flex flex-col items-baseline">
                                                <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">Izračunati popust</label>
                                                <span>{formatCurrency.format(formValues.netDiscount)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-between">
                            {hasDiscount ? (
                                <Button
                                    variant="outlined"
                                    onClick={handleRemoveDiscount}
                                >
                                    Obriši popust
                                </Button>
                            ) : (
                                <div></div>
                            )}
                            <div className="flex gap-3">
                                <Button
                                    variant="outlined"
                                    children={t("admin.close")}
                                    onClick={onClose}
                                />
                                <Button
                                    variant="primary"
                                    children={t("admin.save")}
                                    onClick={onClose}
                                />
                            </div>
                        </div>
                        <Loader isLoading={isLoading}> </Loader>
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};
export default memo(DiscountForm);
